import React, { useEffect, useState } from "react";
import { X, Sparkles, Calendar, Clock, MapPin, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * UpdateKundaliModal — Home hub surface for correcting birth details.
 *
 * STRICT SCOPE (Session 8 spec):
 *   - Only 3 input fields: dob · birth_time · birth_place.
 *   - NO view/download of the Kundali here — that lives on /profile behind Razorpay.
 *   - Submit forces `POST /api/kundali/generate?force=true` under the hood
 *     which overwrites the cache with corrected data.
 */
export default function UpdateKundaliModal({ open, onClose, initial }) {
  const [dob, setDob] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [birthPlace, setBirthPlace] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDob(initial?.dob || "");
    setBirthTime(initial?.birth_time || "");
    setBirthPlace(initial?.birth_place || "");
  }, [open, initial]);

  if (!open) return null;

  const submit = async () => {
    if (!dob) { toast.error("Date of birth is required"); return; }
    setBusy(true);
    try {
      await api.post("/kundali/generate", {
        dob,
        birth_time: birthTime || undefined,
        birth_place: birthPlace || undefined,
        force: true,
        ayanamsa: "lahiri",
      });
      toast.success("Kundali refreshed with your corrected birth details");
      onClose?.(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="update-kundali-modal" className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(24,18,16,0.7)" }}>
      <div className="w-full max-w-md" style={{ background: "var(--ivory)", border: "1px solid var(--gold)" }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid var(--line)" }}>
          <div className="flex items-center gap-2">
            <Sparkles size={16} style={{ color: "var(--crimson)" }}/>
            <p className="font-serif text-xl" style={{ color: "var(--crimson)" }}>Update Kundali Details</p>
          </div>
          <button data-testid="uk-close" onClick={() => onClose?.(false)} style={{ color: "var(--ash)" }}><X size={18}/></button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <p className="text-sm" style={{ color: "var(--ash)" }}>
            Correct or refine your birth details. We&apos;ll refetch the authoritative Kundali from
            Prokerala and overwrite the cache. Viewing / downloading the PDF is on your Profile page.
          </p>

          <div>
            <label className="input-label inline-flex items-center gap-1.5"><Calendar size={12}/> Date of Birth</label>
            <input data-testid="uk-dob" type="date" className="underline-input"
              value={dob} onChange={(e) => setDob(e.target.value)}/>
          </div>

          <div>
            <label className="input-label inline-flex items-center gap-1.5"><Clock size={12}/> Time of Birth</label>
            <input data-testid="uk-birth-time" type="time" className="underline-input"
              value={birthTime} onChange={(e) => setBirthTime(e.target.value)}/>
            <p className="mt-1 text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>
              Accurate to the minute if possible · improves nakshatra precision
            </p>
          </div>

          <div>
            <label className="input-label inline-flex items-center gap-1.5"><MapPin size={12}/> Place of Birth</label>
            <input data-testid="uk-birth-place" type="text" className="underline-input"
              value={birthPlace} onChange={(e) => setBirthPlace(e.target.value)}
              placeholder="e.g., Patna, Bihar"/>
          </div>

          <div className="p-3 flex items-start gap-2 text-[11px]" style={{ background: "rgba(85,127,74,0.06)", border: "1px solid var(--leaf)", color: "var(--ink)" }}>
            <ShieldCheck size={12} style={{ color: "var(--leaf)", marginTop: 2 }}/>
            Each save refetches from the Prokerala API. Please only update when details need correction.
          </div>
        </div>

        <div className="px-6 py-4 flex items-center justify-between" style={{ borderTop: "1px solid var(--line)" }}>
          <button data-testid="uk-cancel" onClick={() => onClose?.(false)}
            className="text-xs uppercase tracking-widest" style={{ color: "var(--ash)" }}>
            Cancel
          </button>
          <button data-testid="uk-submit" onClick={submit} disabled={busy || !dob} className="btn-primary">
            {busy ? "Refreshing…" : "Save & Refresh Kundali"} <RefreshCw size={14}/>
          </button>
        </div>
      </div>
    </div>
  );
}
